import { useEffect, useRef, useState } from 'react';
import { bookingApi, BookingApiError } from '../../lib/api';
import { errorKeyFor, useT } from '../../lib/i18n';
import { ErrorNote, ScreenHeading } from '../shell/PortalShell';
import type { Booking } from '../../lib/types';

/**
 * Where Tap sends the customer back after a deposit.
 *
 * The charge is polled rather than trusted from the URL — a redirect can be
 * replayed or edited, and only the server's own retrieval of the charge decides
 * whether an appointment gets created. Polling backs off and gives up after a
 * bounded number of attempts so a stuck gateway does not spin forever.
 */

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~1 minute

export function PaymentReturn({
  slug,
  chargeId,
  reference,
  onDone,
  onFailed,
}: {
  slug: string;
  /** The charge id, when the return URL or sessionStorage carried it. */
  chargeId: string | null;
  /** Our reference (`pos_ref`), which every gateway's return URL carries. */
  reference: string | null;
  onDone: (booking: Booking) => void;
  onFailed: () => void;
}): JSX.Element {
  const { t } = useT();
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async (): Promise<void> => {
      attempts.current += 1;
      try {
        const result = chargeId
          ? await bookingApi.deposit(slug, chargeId)
          : await bookingApi.depositByRef(slug, reference ?? '');
        if (cancelled) return;

        if (result.status === 'captured' && result.booking) {
          sessionStorage.removeItem(`booking:${slug}:charge`);
          onDone(result.booking);
          return;
        }
        if (result.status === 'failed' || result.status === 'cancelled') {
          sessionStorage.removeItem(`booking:${slug}:charge`);
          setFailed(true);
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setError(t('err_generic'));
          return;
        }
        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        // Stop on a definite answer; keep waiting through transient failures.
        if (err instanceof BookingApiError && err.status >= 400 && err.status < 500) {
          setError(t(errorKeyFor(err.code)));
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setError(t('err_generic'));
          return;
        }
        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slug, chargeId, reference, onDone, t]);

  if (failed) {
    return (
      <div className="bk-rise text-center">
        <ScreenHeading title={t('payment_failed')} subtitle={t('payment_failed_sub')} />
        <button className="bk-btn bk-btn-primary mt-4" onClick={onFailed}>
          {t('retry')}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bk-rise">
        <ErrorNote message={error} onRetry={onFailed} />
      </div>
    );
  }

  return (
    <div className="bk-rise flex flex-col items-center gap-4 py-16 text-center" role="status" aria-live="polite">
      <span
        className="h-10 w-10 animate-spin rounded-full border-2 border-zinc-200"
        style={{ borderTopColor: 'var(--accent)' }}
        aria-hidden
      />
      <p className="m-0 text-sm text-zinc-600">{t('checking_payment')}</p>
    </div>
  );
}
